import { useState } from 'react';
import { HelpCircle, ChevronDown, ChevronUp, Search, MessageCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';

export default function FAQPage() {
  const { language } = useAuth();
  const [openIndex, setOpenIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const faqCategories = [
    {
      category: language === 'hi' ? 'ऑर्डर और डिलीवरी' : 'Orders & Delivery',
      faqs: [
        {
          question:
            language === 'hi'
              ? 'मेरा ऑर्डर कब तक डिलीवर होगा?'
              : 'When will my order be delivered?',
          answer:
            language === 'hi'
              ? 'आमतौर पर ऑर्डर उसी दिन या अगले दिन डिलीवर किए जाते हैं। डिलीवरी का समय सुबह 8 बजे से रात 9 बजे के बीच होता है।'
              : 'Orders are usually delivered on the same day or next day. Delivery time is between 8 AM to 9 PM.',
        },
        {
          question:
            language === 'hi'
              ? 'क्या मैं अपना ऑर्डर ट्रैक कर सकता हूं?'
              : 'Can I track my order?',
          answer:
            language === 'hi'
              ? 'हां, आप "मेरे ऑर्डर" सेक्शन में अपने ऑर्डर की स्थिति देख सकते हैं। हर स्टेटस अपडेट पर आपको ईमेल भी मिलेगा।'
              : 'Yes, you can check your order status in the "My Orders" section. You will also receive email for every status update.',
        },
        {
          question:
            language === 'hi'
              ? 'डिलीवरी शुल्क कितना है?'
              : 'What is the delivery charge?',
          answer:
            language === 'hi'
              ? '₹500 से अधिक के ऑर्डर पर डिलीवरी मुफ्त है। ₹500 से कम के ऑर्डर पर ₹30 डिलीवरी शुल्क लगता है।'
              : 'Delivery is free for orders above ₹500. For orders below ₹500, a delivery charge of ₹30 applies.',
        },
        {
          question:
            language === 'hi'
              ? 'क्या आप मेरे एरिया में डिलीवरी करते हैं?'
              : 'Do you deliver to my area?',
          answer:
            language === 'hi'
              ? 'हम वर्तमान में शामली और आसपास के क्षेत्रों में डिलीवरी करते हैं। अपना पिनकोड दर्ज करके जांच सकते हैं।'
              : 'We currently deliver in Shamli and surrounding areas. You can check by entering your pincode.',
        },
      ],
    },
    {
      category: language === 'hi' ? 'भुगतान' : 'Payment',
      faqs: [
        {
          question:
            language === 'hi'
              ? 'कौन से पेमेंट मेथड उपलब्ध हैं?'
              : 'What payment methods are available?',
          answer:
            language === 'hi'
              ? 'वर्तमान में केवल कैश ऑन डिलीवरी (COD) उपलब्ध है। जल्द ही ऑनलाइन पेमेंट भी उपलब्ध होगा।'
              : 'Currently only Cash on Delivery (COD) is available. Online payment will be available soon.',
        },
        {
          question:
            language === 'hi' ? 'क्या मुझे बिल मिलेगा?' : 'Will I get a bill?',
          answer:
            language === 'hi'
              ? 'हां, डिलीवरी के समय आपको एक डिलीवरी स्लिप मिलेगी जिसमें सभी आइटम और उनकी कीमतें होंगी।'
              : 'Yes, you will receive a delivery slip at the time of delivery with all items and their prices.',
        },
      ],
    },
    {
      category: language === 'hi' ? 'रिटर्न और रिफंड' : 'Returns & Refunds',
      faqs: [
        {
          question:
            language === 'hi'
              ? 'क्या मैं प्रोडक्ट रिटर्न कर सकता हूं?'
              : 'Can I return a product?',
          answer:
            language === 'hi'
              ? 'हां, खराब या गलत उत्पाद डिलीवरी के 24 घंटे के भीतर रिटर्न किए जा सकते हैं। कृपया हमसे संपर्क करें।'
              : 'Yes, damaged or incorrect products can be returned within 24 hours of delivery. Please contact us.',
        },
        {
          question:
            language === 'hi'
              ? 'रिफंड कैसे मिलेगा?'
              : 'How will I get my refund?',
          answer:
            language === 'hi'
              ? 'रिफंड आपके अगले ऑर्डर में क्रेडिट के रूप में दिया जाएगा। यह क्रेडिट स्वचालित रूप से लागू होगा।'
              : 'Refund will be given as credit for your next order. This credit will be applied automatically.',
        },
        {
          question:
            language === 'hi'
              ? 'कौन से प्रोडक्ट रिटर्न नहीं होंगे?'
              : 'Which products cannot be returned?',
          answer:
            language === 'hi'
              ? 'पैकेज खोलने के बाद कुछ उत्पाद (जैसे आटा, मसाले) और फल-सब्जियां स्वाद के आधार पर वापस नहीं होंगी।'
              : 'Some products after opening (like flour, spices) and fruits-vegetables based on taste preference cannot be returned.',
        },
      ],
    },
    {
      category: language === 'hi' ? 'खाता और प्रोफाइल' : 'Account & Profile',
      faqs: [
        {
          question:
            language === 'hi'
              ? 'खाता कैसे बनाएं?'
              : 'How to create an account?',
          answer:
            language === 'hi'
              ? '"रजिस्टर" पर क्लिक करें और अपना नाम, ईमेल, फ़ोन नंबर और पासवर्ड दर्ज करें। आपका खाता तुरंत बन जाएगा।'
              : 'Click on "Register" and enter your name, email, phone number, and password. Your account will be created instantly.',
        },
        {
          question:
            language === 'hi'
              ? 'पासवर्ड भूल गए?'
              : 'Forgot password?',
          answer:
            language === 'hi'
              ? 'वर्तमान में पासवर्ड रीसेट के लिए कृपया हमसे WhatsApp या ईमेल पर संपर्क करें।'
              : 'Currently for password reset, please contact us via WhatsApp or email.',
        },
      ],
    },
  ];

  const allFaqs = faqCategories.flatMap((cat) =>
    cat.faqs.map((faq) => ({ ...faq, category: cat.category }))
  );

  const filteredFaqs = searchQuery
    ? allFaqs.filter(
        (faq) =>
          faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : null;

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="faq-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-600 to-green-700 text-white py-16">
        <div className="container-app">
          <div className="flex items-center gap-3 mb-4">
            <HelpCircle className="w-10 h-10" />
            <h1 className="text-3xl md:text-4xl font-bold">
              {language === 'hi' ? 'सामान्य प्रश्न (FAQ)' : 'Frequently Asked Questions'}
            </h1>
          </div>
          <p className="text-green-100 text-lg max-w-2xl mb-6">
            {language === 'hi'
              ? 'अक्सर पूछे जाने वाले प्रश्नों के उत्तर यहां पाएं।'
              : 'Find answers to commonly asked questions here.'}
          </p>

          {/* Search */}
          <div className="max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder={
                  language === 'hi' ? 'प्रश्न खोजें...' : 'Search questions...'
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white text-slate-900"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-12">
        <div className="container-app">
          <div className="max-w-3xl mx-auto">
            {/* Search Results */}
            {filteredFaqs && (
              <div className="mb-8">
                <p className="text-slate-600 mb-4">
                  {filteredFaqs.length > 0
                    ? `${filteredFaqs.length} ${language === 'hi' ? 'परिणाम मिले' : 'results found'}`
                    : language === 'hi'
                    ? 'कोई परिणाम नहीं मिला'
                    : 'No results found'}
                </p>
                <div className="space-y-3">
                  {filteredFaqs.map((faq, index) => (
                    <div key={index} className="bg-white rounded-xl shadow-sm overflow-hidden">
                      <button
                        onClick={() => toggleFaq(`search-${index}`)}
                        className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition-colors"
                      >
                        <div>
                          <span className="text-xs text-green-600 font-medium">
                            {faq.category}
                          </span>
                          <h3 className="font-medium text-slate-900">{faq.question}</h3>
                        </div>
                        {openIndex === `search-${index}` ? (
                          <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        )}
                      </button>
                      {openIndex === `search-${index}` && (
                        <div className="px-4 pb-4 text-slate-600">{faq.answer}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Categories */}
            {!filteredFaqs && (
              <div className="space-y-8">
                {faqCategories.map((category, catIndex) => (
                  <div key={catIndex}>
                    <h2 className="text-xl font-semibold text-slate-900 mb-4">
                      {category.category}
                    </h2>
                    <div className="space-y-3">
                      {category.faqs.map((faq, faqIndex) => {
                        const index = `${catIndex}-${faqIndex}`;
                        return (
                          <div
                            key={faqIndex}
                            className="bg-white rounded-xl shadow-sm overflow-hidden"
                          >
                            <button
                              onClick={() => toggleFaq(index)}
                              className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition-colors"
                            >
                              <h3 className="font-medium text-slate-900 pr-4">
                                {faq.question}
                              </h3>
                              {openIndex === index ? (
                                <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                              )}
                            </button>
                            {openIndex === index && (
                              <div className="px-4 pb-4 text-slate-600">{faq.answer}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Still Have Questions */}
            <div className="mt-12 bg-green-50 rounded-xl p-6 text-center">
              <MessageCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-900 mb-2">
                {language === 'hi' ? 'अभी भी सवाल है?' : 'Still have questions?'}
              </h2>
              <p className="text-slate-600 mb-4">
                {language === 'hi'
                  ? 'हमारी टीम आपकी मदद के लिए तैयार है।'
                  : 'Our team is ready to help you.'}
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <Link to="/contact">
                  <Button className="bg-green-600 hover:bg-green-700">
                    {language === 'hi' ? 'संपर्क करें' : 'Contact Us'}
                  </Button>
                </Link>
                <a
                  href="https://wa.me/919999999999"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" className="border-green-600 text-green-600">
                    WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
